import React, { useContext, useEffect, useState, useMemo } from 'react';
import { api } from '../../providers/apiClient';
import Page from '../../components/page';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import ModalNewTask from '../../components/modal-task';
import ModalTicketResponse from '../../components/modal-ticket-response';
import SkeletonLoading from '../../components/skeleton-loading';
import { Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  normalizeString,
  formatTimestampToDate,
  toUpperCaseClean,
} from '../../utils';
import { generateServiceDeskReport } from '../../utils/serviceDeskReport';
import { formatTicketCode } from '../../utils/formatCodes';
import { MdOutlineAddTask, MdSearch, MdFilterList } from 'react-icons/md';
import { BsFileText, BsThreeDotsVertical, BsListColumns } from 'react-icons/bs';
import { ContextUser } from '../../providers/ContextUser';
import { buildOrgLevelBreadcrumbs } from '../../utils/breadcrumbUtils';
import { RiCustomerService2Line } from 'react-icons/ri';
import {
  Container,
  CustomTableHead,
  CustomTableBody,
  CustomTableRow,
  CustomTH,
  CustomTD,
  TableBox,
  CustomTable,
  CustomDetailTH,
  ActionsTH,
  CustomButton,
  StatusBadge,
  ServiceDeskHeader,
  FilterSection,
  FilterContainer,
  FilterInputContainer,
  FilterSelectContainer,
  FilterDateContainer,
  CustomInputGroup,
  SearchIconBox,
  CustomFormControl,
  CustomSelect,
  CustomDateInput,
  ListActionButton,
} from './styles';

const PageHelpDesk = () => {
  const { currentOrganization, currentUser } = useContext(ContextUser);
  const [isFetching, setIsFetching] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isResponseModalOpened, setIsResponseModalOpened] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    prioridade: '',
    dataInicio: '',
    dataFim: ''
  });

  const handleToggleModal = () => {
    setIsModalOpened((value) => !value);
  };

  const handleToggleResponseModal = () => {
    setIsResponseModalOpened((value) => !value);
  };

  const handleRespondTask = (task) => {
    setSelectedTask(task);
    handleToggleResponseModal();
  };

  const handleGenerateReport = async () => {
    const reportFilters = {
      search: filters.search,
      status: filters.status,
      priority: filters.prioridade,
      dateFrom: filters.dataInicio,
      dateTo: filters.dataFim,
      totalCount: filteredTickets.length
    };
    
    try {
      setIsGeneratingReport(true);
      await generateServiceDeskReport(filteredTickets, reportFilters);
      
      // Adicionar um pequeno delay para garantir que o spinner seja visível
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error(`Erro ao gerar relatório: ${error.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchBranchSetup();
  }, []);

  // Configurar breadcrumbs quando a organização estiver disponível
  useEffect(() => {
    if (currentOrganization?.id) {
      const breadcrumbs = buildOrgLevelBreadcrumbs(
        currentOrganization,
        'Service Desk',
        '/service-desk'
      );
      setBreadcrumbs(breadcrumbs);
    }
  }, [currentOrganization]);

  async function fetchTasks() {
    if (!currentOrganization?.id) return;
    
    setIsFetching(true);
    try {
      const response = await api.get(`/service_desk`, {
        params: {
          orgId: currentOrganization.id,
        },
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Erro ao buscar tickets:', error);
      } finally {
        setIsFetching(false);
      }
  }

  async function fetchBranchSetup() {
    if (currentOrganization) {
      const orgId = currentOrganization.id;
      await api
        .get(`/orgs/${orgId}/branch_setup`)
        .then((r) => {
          setBranchOptions(r.data);
        })
        .catch(() => {})
        .finally(() => setIsFetching(false));
    }
  }

  // Filtros com useMemo para performance
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      // Filtro de busca por texto
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableText = [
          ticket.resumo,
          ticket.descricao,
          ticket.usuario?.name,
          ticket.usuario?.email,
          ticket.categoria,
          ticket.branch?.name,
          ticket.unit?.name,
          ticket.department?.name
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(searchTerm)) {
          return false;
        }
      }

      // Filtro por status
      if (filters.status && ticket.status !== filters.status) {
        return false;
      }

      // Filtro por prioridade
      if (filters.prioridade && ticket.prioridade !== filters.prioridade) {
        return false;
      }

      // Filtro por data
      if (filters.dataInicio || filters.dataFim) {
        const ticketDate = new Date(ticket.createdAt);
        const dataInicio = filters.dataInicio ? new Date(filters.dataInicio) : null;
        const dataFim = filters.dataFim ? new Date(filters.dataFim) : null;

        if (dataInicio && ticketDate < dataInicio) {
          return false;
        }
        if (dataFim && ticketDate > dataFim) {
          return false;
        }
      }

      return true;
    });
  }, [tickets, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      prioridade: '',
      dataInicio: '',
      dataFim: ''
    });
  };

  const hasActiveFilters = filters.search || filters.status || filters.prioridade || filters.dataInicio || filters.dataFim;

  const buildTableHead = () => {
    const columnHeaders = [
      'Código',
      'Data de Abertura',
      'Assunto',
      'Usuário',
      'Secretaria',
      'Status/Prioridade',
      '...',
    ];
    return (
      <CustomTableHead id='table-head'>
        <CustomTableRow id='table-head-row'>
          {columnHeaders.map((header, index) => (
            header === '...'
              ? (
                <ActionsTH key={index} id='th-acoes'>
                  
                </ActionsTH>
              ) : (
                <CustomTH key={index} id={`th-${normalizeString(header)}`}>
                  {header}
                </CustomTH>
              )
          ))}
        </CustomTableRow>
      </CustomTableHead>
    );
  };

  const buildTableBody = () => {
    return (
      <CustomTableBody id='table-body'>
        {filteredTickets.map((ticket) => (
          <CustomTableRow
            id={`table-body-row-${ticket.id}`}
            key={ticket.id}
          >
            <CustomTD>{formatTicketCode(ticket.id)}</CustomTD>
            <CustomTD>{formatTimestampToDate(ticket.createdAt)}</CustomTD>
            <CustomTD>
              <div 
                className="text-truncate" 
                style={{ maxWidth: '200px' }}
                title={ticket.resumo}
              >
                {ticket.resumo}
              </div>
            </CustomTD>
            <CustomTD>{ticket.usuario?.name || 'N/A'}</CustomTD>
            <CustomTD>{ticket.branch?.name || 'N/A'}</CustomTD>
            <CustomTD>
              <div className="d-flex flex-row gap-2 align-items-center">
                <StatusBadge className={`status-${ticket.status?.toLowerCase().replace('_', '-') || 'aberto'}`}>
                  {ticket.status || 'ABERTO'}
                </StatusBadge>
                {ticket.prioridade && (
                  <StatusBadge className={`prioridade-${ticket.prioridade.toLowerCase()}`}>
                    {ticket.prioridade}
                  </StatusBadge>
                )}
              </div>
            </CustomTD>
            <CustomTD className="text-center">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <ListActionButton
                  className='action-button secondary'
                  onClick={() => handleRespondTask(ticket)}
                  id={`list-view-ticket-${ticket.id}`}
                  title='Detalhes do chamado'
                >
                  <BsListColumns />
                  Detalhes
                </ListActionButton>
              </div>
            </CustomTD>
          </CustomTableRow>
        ))}
      </CustomTableBody>
    );
  };

  const buildTableSkeleton = () => {
    return (
      <CustomTableBody id='table-skeleton-body'>
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <CustomTableRow key={`skeleton-row-${item}`} id={`skeleton-row-${item}`}>
            <CustomTD>
              <SkeletonLoading id={`skeleton-code-${item}`} height='14px' width='50px' />
            </CustomTD>
            <CustomTD>
              <SkeletonLoading id={`skeleton-date-${item}`} height='14px' width='70px' />
            </CustomTD>
            <CustomTD>
              <SkeletonLoading id={`skeleton-subject-${item}`} height='14px' width='120px' />
            </CustomTD>
            <CustomTD>
              <SkeletonLoading id={`skeleton-user-${item}`} height='14px' width='90px' />
            </CustomTD>
            <CustomTD>
              <SkeletonLoading id={`skeleton-branch-${item}`} height='14px' width='70px' />
            </CustomTD>
            <CustomTD>
              <div className="d-flex flex-row gap-2 align-items-center">
                <SkeletonLoading id={`skeleton-status-${item}`} height='18px' width='60px' />
                <SkeletonLoading id={`skeleton-priority-${item}`} height='18px' width='45px' />
              </div>
            </CustomTD>
            <CustomTD className="text-center">
              <SkeletonLoading id={`skeleton-button-${item}`} height='28px' width='28px' />
            </CustomTD>
          </CustomTableRow>
        ))}
      </CustomTableBody>
    );
  };

  const ButtonNewTask = (
    <CustomButton
      key='btnAddTask'
      onClick={handleToggleModal}
      type='button'
      style={{
        display: 'flex',
        alignItems: 'center',
        zIndex: '99',
        fontSize: 13,
      }}
    >
      <MdOutlineAddTask
        style={{
          fontSize: 20,
          marginRight: 10,
        }}
      />
      Novo chamado
    </CustomButton>
  );

  const ButtonGenerateReport = (
    <CustomButton
      key='btnGenerateReport'
      onClick={handleGenerateReport}
      type='button'
      disabled={isGeneratingReport}
      style={{
        display: 'flex',
        alignItems: 'center',
        zIndex: '99',
        fontSize: 13,
        marginLeft: 10,
        opacity: isGeneratingReport ? 0.7 : 1,
      }}
    >
      {isGeneratingReport ? (
        <>
          <Spinner
            animation="border"
            size="sm"
            style={{
              marginRight: 10,
              width: '16px',
              height: '16px',
            }}
          />
          Gerando...
        </>
      ) : (
        <>
          <BsFileText
            style={{
              fontSize: 20,
              marginRight: 10,
            }}
          />
          Relatório Service Desk
        </>
      )}
    </CustomButton>
  );

  return (
    <>
      <Breadcrumbs id='breadcrumbs-service-desk' path={breadcrumbs} />
      <Page title={'Service Desk'} description="Central de atendimento para registro e acompanhamento de chamados internos da prefeitura." icon={<RiCustomerService2Line id='icon-service-desk-page' />} sideAction={[ButtonNewTask, ButtonGenerateReport]}>
        <Container id='table-container'>
          {/* Filtros */}
          <ServiceDeskHeader id='service-desk-header'>
            <FilterSection id='service-desk-filter-section'>
              <FilterContainer id='filter-container'>
                <FilterInputContainer id='filter-input-container'>
                  <CustomInputGroup id='search-input'>
                    <SearchIconBox>
                      <MdSearch />
                    </SearchIconBox>
                    <CustomFormControl
                      type="text"
                      placeholder="Buscar por assunto, descrição, usuário..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </CustomInputGroup>
                </FilterInputContainer>
                
                <FilterSelectContainer id='filter-status-container'>
                  <CustomSelect
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">Busque por status...</option>
                    <option value="ABERTO">Aberto</option>
                    <option value="EM_ANDAMENTO">Em Andamento</option>
                    <option value="CONCLUIDO">Concluído</option>
                    <option value="CANCELADO">Cancelado</option>
                  </CustomSelect>
                </FilterSelectContainer>
                
                <FilterSelectContainer id='filter-priority-container'>
                  <CustomSelect
                    value={filters.prioridade}
                    onChange={(e) => handleFilterChange('prioridade', e.target.value)}
                  >
                    <option value="">Busque por prioridade...</option>
                    <option value="BAIXA">Baixa</option>
                    <option value="MEDIA">Média</option>
                    <option value="ALTA">Alta</option>
                  </CustomSelect>
                </FilterSelectContainer>
                
                <FilterDateContainer id='filter-date-start-container'>
                  <CustomDateInput
                    type="date"
                    value={filters.dataInicio}
                    onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
                  />
                </FilterDateContainer>
                
                <FilterDateContainer id='filter-date-end-container'>
                  <CustomDateInput
                    type="date"
                    value={filters.dataFim}
                    onChange={(e) => handleFilterChange('dataFim', e.target.value)}
                  />
                </FilterDateContainer>
              </FilterContainer>
            </FilterSection>
            {hasActiveFilters && (
              <CustomButton id='service-desk-clear-filters-btn' onClick={clearFilters}>
                <MdFilterList />
                Limpar Filtros
              </CustomButton>
            )}
          </ServiceDeskHeader>

          {/* Tabela */}
          <TableBox className='table-responsive'>
            <CustomTable striped hover>
              {buildTableHead()}
              {isFetching ? buildTableSkeleton() : buildTableBody()}
            </CustomTable>
          </TableBox>

          {/* Contador de resultados */}
          {!isFetching && (
            <div className="mt-3 text-muted text-center">
              Mostrando {filteredTickets.length} de {tickets.length} tickets
            </div>
          )}
        </Container>
      </Page>
      <ModalNewTask
        isModalOpened={isModalOpened}
        handleToggleModal={handleToggleModal}
        fetchTasks={fetchTasks}
        branchOptions={branchOptions}
        organization={currentOrganization}
        user={currentUser}
      />
      {selectedTask && (
        <ModalTicketResponse
          isModalOpened={isResponseModalOpened}
          handleToggleModal={handleToggleResponseModal}
          ticket={selectedTask}
          onTicketUpdate={fetchTasks}
        />
      )}
    </>
  );
};

export default PageHelpDesk;
